// @flow
import * as React from 'react';
import { sprintf } from 'sprintf-js';
import { LISTING_TYPE_HUMAN_NAME_MAP } from 'utils/constants';
import ClipboardButton from 'react-clipboard.js';
import styled from 'styled-components';
import queryString from 'query-string';
import { trackClicked } from 'utils/analytics';
import { updateObject, entries } from 'utils/functional';
import { getShortUrl } from 'api/website';
import type { ShortUrlType } from 'api/website';
import Box from 'components/Box';
import { transition, colors } from 'theme';

const { useEffect, useState } = React;

// {a: 1, b: 2} => [{name: 'a', value: 1}, {name: 'b', value: 2}]
const makeParameters = (params: { [string]: string }) =>
  entries(params).map(([key, value]) => ({ name: key, value }));

const socialLink = (socialType, link, message, listingType) => {
  switch (socialType) {
    case 'facebook': {
      const qs = queryString.stringify(
        updateObject({ u: link }, message ? { title: message } : {})
      );
      return `https://facebook.com/sharer/sharer.php?${qs}`;
    }
    case 'twitter': {
      const qs = queryString.stringify({ text: message, url: link });
      return `https://twitter.com/intent/tweet/?${qs}`;
    }
    case 'email': {
      const qs = queryString.stringify({
        subject: message,
        body: `${
          listingType === 'CMS_PAGE'
            ? getText('Click the link below to see the article on Idealist:')
            : listingType === 'URL'
            ? getText('Click the link below to see the article from Idealist:')
            : listingType
            ? sprintf(
                getText(
                  'Click the link below to see the %(type)s listing on Idealist:'
                ),
                {
                  type: LISTING_TYPE_HUMAN_NAME_MAP[listingType]
                }
              )
            : getText('Click the link below to see the page on Idealist:')
        }\n\n${link}`
      });
      return `mailto:?${qs}`;
    }
    case 'linkedin': {
      const qs = queryString.stringify({
        url: link,
        mini: true,
        title: message
      });
      return `https://www.linkedin.com/shareArticle?${qs}`;
    }
    default:
      throw new Error(`Invalid social type ${socialType}`);
  }
};

const withSocialUtm = (params: { [string]: string }, socialType: string) => ({
  ...params,
  utm_source: socialType,
  utm_campaign: 'visitorshare',
  utm_medium: 'social'
});

const socialWindowFeatures =
  'scrollbars=yes,menubar=no,width=700,height=600,resizable=yes,toolbar=no,location=no,status=no';

type SocialType = 'facebook' | 'twitter' | 'email' | 'linkedin';

const ShareStyleButton = styled.a`
  border-radius: 3px;
  color: white;
  text-decoration: none;
  &:hover,
  &:focus {
    text-decoration: none;
    color: white;
  }
`;

export const CopyClipboardButton = ({
  object,
  objectType,
  ...props
}: {
  object: Object,
  objectType: ShortUrlType
}) => {
  const [shortUrl, setShortUrl] = useState(null);
  useEffect(
    () => {
      getShortUrl({
        toType: objectType,
        toId: object.id
      }).then(url => setShortUrl(url));
    },
    [objectType, object.id]
  );
  return (
    shortUrl && (
      <ShareStyleButton
        {...props}
        component={ClipboardButton}
        data-clipboard-text={shortUrl}
      />
    )
  );
};

export const ShareButton = ({
  eventName,
  eventProperties,
  object,
  objectType,
  socialType,
  utmParams,
  message,
  onClick,
  ...props
}: {
  eventName: string,
  eventProperties?: Object,
  object: Object,
  objectType?: ShortUrlType,
  socialType: SocialType,
  utmParams: { [string]: string },
  message?: string,
  onClick?: () => void
}) => {
  const clickHandler = e => {
    e.preventDefault();
    trackClicked(eventName, eventProperties || {});
    let linkPs;
    if (objectType) {
      linkPs = getShortUrl({
        toType: objectType,
        toId: object.id,
        extraParams: makeParameters(withSocialUtm(utmParams, socialType))
      });
    } else {
      // Only use link shortener for Idealist links
      linkPs = object.url.startsWith('/')
        ? getShortUrl({
            toType: 'URL',
            toUrl: object.url,
            extraParams: makeParameters(withSocialUtm(utmParams, socialType))
          })
        : // No shortening for links that don't start with '/'
          Promise.resolve(object.url);
    }
    linkPs.then(shortResourceLink => {
      const link = socialLink(
        socialType,
        shortResourceLink,
        message,
        objectType
      );
      window.open(link, '', socialWindowFeatures);
    });

    if (onClick) {
      onClick();
    }
  };
  return (
    <Box
      component={ShareStyleButton}
      data-qa-id={`social-button-${socialType}`}
      display="inline-block"
      onClick={clickHandler}
      href={
        object.url
          ? socialLink(
              socialType,
              objectType
                ? `https://idealist.org/${
                    objectType === 'CMS_PAGE'
                      ? object.url
                      : object.url[USER_LOCALE]
                  }`
                : object.url,
              message,
              objectType
            )
          : ''
      }
      target="_blank"
      {...props}
    />
  );
};

export const SocialButton = styled(ShareButton)`
  display: block;
  margin: 6px;
  border-radius: 50%;
  background: ${colors.platinum};
  color: ${colors.battleshipGrey};
  transition: all ${transition};
  &:hover,
  &:focus {
    background: ${colors.waterBlue};
    color: white;
  }
`;
